import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, ArrowUpRight, TriangleAlert } from "lucide-react";
import { useLaunchBoardroom } from "@/lib/onboarding";

const ITEMS = [
  { title: "Sign Delaware incorporation docs", via: "Stripe Atlas →" },
  { title: "Open business bank account", via: "Mercury →" },
  { title: "Founder IP assignment", via: "Clerky →" },
];

export default function HitlSection() {
  const launch = useLaunchBoardroom();
  return (
    <section
      data-testid="hitl-section"
      className="overflow-hidden bg-[#0B0B0E] px-5 py-28 text-white md:px-8 md:py-36"
    >
      <div className="mx-auto max-w-6xl text-center">
        <motion.div
          initial={{ opacity: 0, y: 14 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.5 }}
        >
          <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-zinc-400">
            Human-in-the-loop
          </div>
          <h2 className="mt-4 text-[44px] font-black leading-[1.02] tracking-tight text-white md:text-[80px]">
            You stay in command.<br />
            <span className="text-zinc-500">Always.</span>
          </h2>
          <p className="mx-auto mt-6 max-w-2xl text-[15px] leading-relaxed text-zinc-400 md:text-[17px]">
            Anything an agent can't do alone — sign documents, file paperwork,
            talk to humans — gets flagged the moment it's needed, with the exact
            next step.
          </p>
        </motion.div>

        {/* Glass card */}
        <motion.div
          initial={{ opacity: 0, y: 24, rotate: 0 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.6, delay: 0.1 }}
          data-testid="hitl-glass-card"
          className="mx-auto mt-14 max-w-2xl overflow-hidden rounded-[28px] border border-white/10 bg-white/5 p-1.5 backdrop-blur-2xl shadow-[0_40px_80px_-40px_rgba(124,58,237,0.6)] md:[transform:rotate(-1.5deg)]"
        >
          <div className="overflow-hidden rounded-[22px] border border-[#FFE0B2]/40 bg-[#FFF7EE] text-zinc-900">
            <div className="flex items-start gap-3 px-5 pt-5">
              <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl bg-orange-500/20 text-orange-700">
                <TriangleAlert className="h-5 w-5" strokeWidth={2.4} />
              </div>
              <div className="text-left">
                <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-orange-700">
                  Human action required · 3
                </div>
                <div className="mt-0.5 text-[14px] font-bold text-zinc-900">
                  3 tasks blocking your boardroom
                </div>
              </div>
            </div>
            <ul className="mt-4 divide-y divide-[#FFE0B2]/70">
              {ITEMS.map((it, i) => (
                <li
                  key={i}
                  data-testid={`hitl-item-${i}`}
                  className="flex items-center justify-between gap-3 px-5 py-3 text-left"
                >
                  <span className="text-[13px] font-semibold text-zinc-900">
                    {it.title}
                  </span>
                  <span className="inline-flex items-center gap-1 text-[12px] font-semibold text-orange-700">
                    {it.via}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 12 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.5, delay: 0.2 }}
          className="mt-12 flex flex-col items-center justify-center gap-3 sm:flex-row"
        >
          <button
            type="button"
            onClick={launch}
            data-testid="hitl-cta-boardroom"
            className="group inline-flex h-11 items-center gap-2 rounded-full bg-white px-5 text-[13px] font-semibold text-zinc-900 transition-transform hover:-translate-y-0.5"
          >
            Launch boardroom
            <ArrowRight
              className="h-4 w-4 transition-transform group-hover:translate-x-0.5"
              strokeWidth={2.4}
            />
          </button>
          <Link
            to="/tasks"
            data-testid="hitl-cta-tasks"
            className="group inline-flex h-11 items-center gap-2 rounded-full border border-white/20 bg-white/5 px-5 text-[13px] font-semibold text-white transition-colors hover:bg-white/10"
          >
            See human-in-the-loop tasks
            <ArrowUpRight
              className="h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
              strokeWidth={2.4}
            />
          </Link>
        </motion.div>
      </div>
    </section>
  );
}
